import dayjs from 'dayjs'

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토']
const KST_OFFSET_MS = 9 * 60 * 60 * 1000

/**
 * dayjs 객체 생성 (안전한 파싱)
 * @param {string|Date} date
 * @returns {dayjs.Dayjs}
 */
const toDayjs = (date) => {
  return dayjs(date)
}

/**
 * 요일명 반환
 * @param {string|Date} date
 * @returns {string} '일'|'월'|...|'토'
 */
export const getWeekday = (date) => {
  return WEEKDAYS[toDayjs(date).day()]
}

// =============================================================================
// 포맷 유형 1: YYYY-MM-DD
// =============================================================================
/**
 * @param {string|Date} date
 * @returns {string} e.g. '2025-08-29'
 */
export const formatYMD = (date) => {
  return toDayjs(date).format('YYYY-MM-DD')
}

/**
 * 시간대가 포함된 값을 한국 날짜(YYYY-MM-DD)로 변환
 * @param {string|Date|number} date
 * @returns {string} e.g. '2026-09-21'
 */
export const formatKstYMD = (date) => {
  if (!date) { return '' }

  const value = String(date)
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) { return value }
  if (/^(\d{8}|\d{14})$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
  }

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) { return '' }

  const kstDate = new Date(parsedDate.getTime() + KST_OFFSET_MS)
  const year = kstDate.getUTCFullYear()
  const month = String(kstDate.getUTCMonth() + 1).padStart(2, '0')
  const day = String(kstDate.getUTCDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * 기준 일자가 한국 날짜 기준 시작일과 종료일 사이인지 확인
 * @param {string|Date|number} startDate
 * @param {string|Date|number} endDate
 * @param {string|Date|number} targetDate
 * @returns {boolean}
 */
export const isInKstDateRange = (startDate, endDate, targetDate = new Date()) => {
  const start = formatKstYMD(startDate)
  const end = formatKstYMD(endDate)
  const target = formatKstYMD(targetDate)
  return Boolean(start && end && target && target >= start && target <= end)
}

// =============================================================================
// 포맷 유형 2: MM/DD(요일)
// =============================================================================
/**
 * @param {string|Date} date
 * @returns {string} e.g. '08/29(금)'
 */
export const formatMDWeekday = (date) => {
  const d = toDayjs(date)
  return d.format('M/D') + '(' + WEEKDAYS[d.day()] + ')'
}

// =============================================================================
// 포맷 유형 3: YYYY-MM-DD ~ YYYY-MM-DD
// =============================================================================
/**
 * @param {string|Date} startDate
 * @param {string|Date} endDate
 * @returns {string} e.g. '2025-08-29 ~ 2025-09-05'
 */
export const formatYMDRange = (startDate, endDate) => {
  return formatYMD(startDate) + ' ~ ' + formatYMD(endDate)
}

// =============================================================================
// 포맷 유형 4: M/D(요일) ~ M/D(요일)
// =============================================================================
/**
 * @param {string|Date} startDate
 * @param {string|Date} endDate
 * @returns {string} e.g. '8/29(금) ~ 9/5(금)'
 */
export const formatMDWeekdayRange = (startDate, endDate) => {
  const s = toDayjs(startDate)
  const e = toDayjs(endDate)
  const sStr = s.format('M/D') + '(' + WEEKDAYS[s.day()] + ')'
  const eStr = e.format('M/D') + '(' + WEEKDAYS[e.day()] + ')'
  return sStr + ' ~ ' + eStr
}

// =============================================================================
// 포맷 유형 5: YYYY-MM-DD HH:mm:ss
// =============================================================================
/**
 * @param {string|Date} date
 * @returns {string} e.g. '2025-08-29 14:30:00'
 */
export const formatYMDHms = (date) => {
  return toDayjs(date).format('YYYY-MM-DD HH:mm:ss')
}

// =============================================================================
// 포맷 유형 6: YYYY-MM-DD HH:mm
// =============================================================================
/**
 * @param {string|Date} date
 * @returns {string} e.g. '2025-08-29 14:30'
 */
export const formatYMDHm = (date) => {
  return toDayjs(date).format('YYYY-MM-DD HH:mm')
}

// =============================================================================
// 포맷 유형 7: MM/DD(요일) HH:mm:ss
// =============================================================================
/**
 * @param {string|Date} date
 * @returns {string} e.g. '08/29(금) 14:30:00'
 */
export const formatMDWeekdayHms = (date) => {
  const d = toDayjs(date)
  return d.format('M/D') + '(' + WEEKDAYS[d.day()] + ') ' + d.format('HH:mm:ss')
}
